export const localeText = {
  en: {
    languageName: 'English',
    languageLabel: 'Language',
    hero: {
      eyebrow: 'Need the HNS wallet, registrar, DNSSEC, and TLSA records?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'DNS-based Authentication of Named Entities',
      steps: {
        enter: 'Enter the name, nameserver, server IP, and certificate here.',
        send: 'Send the generated records to your HNS wallet or registrar.',
        zone: 'Copy the zone records onto the authoritative DNS server.',
        done: 'Publish DNSSEC and TLSA so the DANE website can serve.'
      }
    },
    sections: {
      domain: '1. Domain',
      server: '2. Server',
      dane: '3. DANE'
    },
    fields: {
      domainType: 'Domain type',
      domainTypeHelp: 'HNS means wallet/name resource. ICANN means registrar panel.',
      setupMode: 'Setup mode',
      setupModeHelp: 'Delegated mode points at a nameserver hostname. SYNTH stores nameserver IPs in HNS; both still need signed authoritative DNS.',
      domain: 'Domain',
      domainHelp: 'Examples: dane/ or example.com',
      hnsDomainHelp: 'HNS names must end with / and use one lowercase ASCII name: a-z, 0-9, - or _, with - and _ only in the middle.',
      dnsServerPreset: 'DNS server preset',
      dnsServerPresetHelp: 'This changes the copy-paste server example, not the DNS meaning.',
      nameserverHost: 'Nameserver hostname',
      nameserverHostHelp: 'Use provider nameservers, or use ns1.yourname. with glue if the nameserver lives under the same name.',
      nameserverIpv4: 'Nameserver IPv4',
      nameserverIpv4Help: 'Required for HNS SYNTH and in-zone delegated nameservers; optional for external delegated nameservers.',
      nameserverIpv6: 'Nameserver IPv6 (optional)',
      nameserverIpv6Help: 'Optional IPv6 glue/address for the DNS server.',
      websiteIpv4: 'Website IPv4',
      websiteIpv4Help: 'The public IPv4 address of the web server at the domain apex.',
      websiteIpv6: 'Website IPv6 (optional)',
      websiteIpv6Help: 'Optional public IPv6 address of the web server at the domain apex.',
      port: 'HTTPS port',
      portHelp: '443 is the default for normal HTTPS. Change it only if the TLS service uses another port.',
      certificate: 'TLSA certificate or PUBLIC KEY',
      certificateHelp: 'Paste the leaf certificate or PEM PUBLIC KEY here to generate the TLSA record. Private keys are not needed.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'After DNSSEC signing is enabled on the DNS server, paste the zone DNSKEY here to generate DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'Delegated authoritative DNS',
      hnsInline: 'HNS SYNTH nameserver',
      genericZone: 'Generic zone file',
      hostedDns: 'Hosted DNS provider panel',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'How to get this',
      certIntro: 'This is the TLSA source field. Paste the public certificate your TLS server serves for this exact hostname, port, protocol, and SNI name, or paste the matching PEM PUBLIC KEY. Do not paste a private key.',
      certFetch: 'If you have shell access, fetch the leaf certificate from the live service:',
      certFile: 'If you already have a certificate file, paste the block from BEGIN CERTIFICATE through END CERTIFICATE. To paste only the public key from a certificate file, run:',
      dnskeyIntro: 'First enable DNSSEC signing on the exact authoritative child zone being delegated. The DNS server or hosted DNS provider creates the public DNSKEY; this app uses that public record to generate the parent-side DS record.',
      dnskeyHosted: 'In a hosted DNS panel, look for DNSSEC, DS, or DNSKEY settings. If the provider gives you a DS record directly, you can publish that DS at the wallet or registrar without pasting DNSKEY here.',
      dnskeyQuery: 'If your authoritative server is already answering, query the public DNSKEY and paste the DNSKEY line for this zone, usually the key-signing key with flags 257:'
    },
    copy: {
      nothing: '# Nothing generated yet'
    },
    linode: {
      provisioningTag: 'One-click provisioning',
      cardLink: 'HNS DANE server on Linode/Akamai',
      cardBody: 'One-click HNS DANE server for a single Handshake name. Installs Knot DNS authoritative DNSSEC, TLSA, nginx dashboard, local verification, and copy-paste HNS resource commands. Never asks for wallet seeds, Linode API tokens, registrar credentials, or payment data.',
      deployTitle: 'Deploy on Linode/Akamai',
      beginHosting: 'Begin hosting',
      publishSetup: 'Publish setup',
      guide: 'Guide',
      stackScript: 'StackScript'
    },
    status: {
      title: 'Setup status',
      ok: 'OK',
      warn: 'Check',
      missing: 'Needed',
      labels: {
        Domain: 'Domain',
        'Website IP': 'Website IP',
        'HNS inline': 'HNS SYNTH',
        DANE: 'DANE',
        Nameserver: 'Nameserver',
        Glue: 'Glue',
        DS: 'DS',
        TLSA: 'TLSA'
      }
    },
    notices: {
      title: 'Needs attention'
    },
    summary: {
      aria: 'current setup summary',
      hnsInline: 'HNS SYNTH nameserver',
      delegated: 'Delegated DNSSEC + DANE',
      glueRequired: 'Glue required',
      externalNameserver: 'External nameserver',
      dsReady: 'DS ready',
      dsPlaceholder: 'DS placeholder',
      tlsaReady: 'TLSA ready',
      tlsaPlaceholder: 'TLSA placeholder'
    },
    output: {
      parentHns: 'Put this in your HNS wallet / name resource',
      parentIcann: 'Put this at your registrar / parent zone',
      authoritative: 'Put this on your authoritative DNS server',
      steps: 'Do these steps',
      verify: 'Verify with these commands',
      web: 'Web server note',
      integrator: 'Integrator JSON',
      server: 'Server preset',
      audiences: {
        parent: 'parent',
        authoritative: 'authoritative',
        server: 'server',
        web: 'web',
        verify: 'verify',
        integrator: 'integrator'
      }
    },
    faq: {
      title: 'Help tips',
      setupModeSummary: 'Which setup mode should I use?',
      setupModeBody: 'Use Delegated authoritative DNS for the DANE setup path: create or choose an authoritative DNS zone, put its nameserver hostname in the wallet or registrar, add glue if that hostname is under the same name, enable DNSSEC, publish the DS at the parent, and publish A/AAAA plus TLSA in the signed zone. Use HNS SYNTH only when an HNS name should store the authoritative nameserver IP directly; SYNTH is still just the HNS nameserver referral for the same signed DANE zone.',
      domainSummary: 'What domain format should I enter?',
      domainBody: 'For HNS, enter one lowercase name ending in /, for example dane/. Do not include child labels such as www.dane/ or any internal /. For ICANN, enter the DNS domain name, for example example.com.',
      presetSummary: 'Which preset should I pick?',
      presetBody: 'Use Hosted DNS if your provider supports DNSSEC and TLSA records. Use Generic zone file when you are adapting records into BIND, Knot, NSD, or another server. Use PowerDNS when you want database or API-backed DNS.',
      splitSummary: 'What goes in the wallet versus the DNS server?',
      splitBody: 'The wallet or registrar gets NS/GLUE or SYNTH, plus DS. The authoritative DNS server gets NS/A/AAAA/TLSA and signs the zone. TLSA is a DNS-server record, not a wallet record.',
      nameserverIpv4Summary: 'When do I need a nameserver IP?',
      nameserverIpv4Body: 'Use the public IPv4 address of the authoritative DNS server. In HNS SYNTH mode this becomes SYNTH4. In delegated mode it is only parent-side GLUE4 when the nameserver hostname lives inside the same name or zone.',
      websiteIpv4Summary: 'Is this the SYNTH or glue IP?',
      websiteIpv4Body: 'No. Website IPv4 becomes the A record for the site on the authoritative DNS server. SYNTH and glue are for reaching the nameserver; website A/AAAA records point browsers to the web server.',
      dnskeySummary: 'When do I paste the DNSKEY?',
      dnskeyBody: 'Paste DNSKEY after the exact authoritative child zone is DNSSEC-signed. This page uses it to generate the parent-side DS record; it does not sign the zone or store private DNSSEC keys. Validate the chain after the DS is published.',
      idnSummary: 'Can I use an internationalized domain name?',
      idnBody: 'Yes. Unicode domain input is converted to IDNA ASCII A-labels such as xn--bcher-kva.example. Use the A-label form in DNS records, wallet fields, registrar fields, and server configs.',
      hostedSummary: 'What must a hosted DNS provider support?',
      hostedBody: 'The provider needs authoritative DNS hosting, DNSSEC signing, DS or DNSKEY export, custom TLSA records, signature refresh, authenticated denial records, and enough diagnostics to confirm DNSSEC validation. If a provider cannot publish TLSA in a signed zone, it cannot complete this DANE path.'
    }
  },
  es: {
    languageName: 'Español',
    languageLabel: 'Idioma',
    hero: {
      eyebrow: '¿Necesitas registros HNS, registrador, DNSSEC y TLSA?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'Autenticación basada en DNS de entidades nombradas',
      steps: {
        enter: 'Introduce aquí el nombre, el servidor de nombres, la IP del servidor y el certificado.',
        send: 'Envía los registros generados a tu cartera HNS o registrador.',
        zone: 'Copia los registros de zona en el servidor DNS autoritativo.',
        done: 'Publica DNSSEC y TLSA para que el sitio DANE pueda servir.'
      }
    },
    sections: { domain: '1. Dominio', server: '2. Servidor', dane: '3. DANE' },
    fields: {
      domainType: 'Tipo de dominio',
      domainTypeHelp: 'HNS significa cartera/recurso de nombre. ICANN significa panel del registrador.',
      setupMode: 'Modo de configuración',
      setupModeHelp: 'El modo delegado apunta a un hostname de nameserver. SYNTH guarda IPs en HNS; ambos necesitan DNS autoritativo firmado.',
      domain: 'Dominio',
      domainHelp: 'Ejemplos: dane/ o example.com',
      hnsDomainHelp: 'Los nombres HNS deben terminar en / y usar un solo nombre ASCII en minúsculas: a-z, 0-9, - o _, con - y _ solo en medio.',
      dnsServerPreset: 'Plantilla de servidor DNS',
      dnsServerPresetHelp: 'Cambia el ejemplo para copiar y pegar, no el significado DNS.',
      nameserverHost: 'Nombre del servidor DNS',
      nameserverHostHelp: 'Usa nameservers del proveedor, o ns1.tunombre. con glue si el nameserver vive bajo el mismo nombre.',
      nameserverIpv4: 'IPv4 del servidor DNS',
      nameserverIpv4Help: 'Obligatoria para SYNTH HNS y nameservers delegados dentro de la zona; opcional para nameservers delegados externos.',
      nameserverIpv6: 'IPv6 del servidor DNS (opcional)',
      nameserverIpv6Help: 'Glue/dirección IPv6 opcional para el servidor DNS.',
      websiteIpv4: 'IPv4 del sitio web',
      websiteIpv4Help: 'La IPv4 pública del servidor web en el apex del dominio.',
      websiteIpv6: 'IPv6 del sitio web (opcional)',
      websiteIpv6Help: 'IPv6 pública opcional del servidor web en el apex del dominio.',
      port: 'Puerto HTTPS',
      portHelp: '443 es el valor predeterminado para HTTPS normal. Cámbialo solo si el servicio TLS usa otro puerto.',
      certificate: 'Certificado o PUBLIC KEY',
      certificateHelp: 'Pega el certificado de hoja o una PUBLIC KEY en PEM. No se necesitan claves privadas.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'Después de activar la firma DNSSEC en el servidor DNS, pega aquí la DNSKEY de la zona para generar DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS autoritativo delegado',
      hnsInline: 'Nameserver SYNTH HNS',
      genericZone: 'Archivo de zona genérico',
      hostedDns: 'Panel de proveedor DNS alojado',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'Cómo obtener esto',
      certIntro: 'Pega el certificado público que tu servidor HTTPS entrega para este nombre exacto, o pega la PUBLIC KEY en PEM correspondiente. No pegues una clave privada.',
      certFetch: 'Si tienes acceso de shell, obtén el certificado de hoja desde el servicio activo:',
      certFile: 'Si ya tienes un archivo de certificado, pega el bloque desde BEGIN CERTIFICATE hasta END CERTIFICATE. Para pegar solo la clave pública desde un certificado, ejecuta:',
      dnskeyIntro: 'Primero activa la firma DNSSEC en la zona DNS autoritativa. El servidor DNS o proveedor alojado crea la DNSKEY pública; esta app usa ese registro público para generar el DS del padre.',
      dnskeyHosted: 'En un panel DNS alojado, busca opciones DNSSEC, DS o DNSKEY. Si el proveedor te da directamente un registro DS, puedes publicarlo en la cartera o registrador sin pegar DNSKEY aquí.',
      dnskeyQuery: 'Si tu servidor autoritativo ya responde, consulta la DNSKEY pública y pega la línea DNSKEY de la zona, normalmente la clave de firma con flags 257:'
    },
    copy: { nothing: '# Aún no se generó nada' },
    linode: {
      provisioningTag: 'Aprovisionamiento con un clic',
      cardLink: 'Servidor HNS DANE en Linode/Akamai',
      cardBody: 'Servidor HNS DANE con un clic para un solo nombre Handshake. Instala Knot DNS autoritativo con DNSSEC, TLSA, panel nginx, verificación local y comandos de recurso HNS para copiar y pegar. Nunca pide semillas de cartera, tokens API de Linode, credenciales de registrador ni datos de pago.',
      deployTitle: 'Desplegar en Linode/Akamai',
      beginHosting: 'Comenzar hosting',
      publishSetup: 'Publicar configuración',
      guide: 'Guía',
      stackScript: 'StackScript'
    },
    status: {
      title: 'Estado de configuración',
      ok: 'OK',
      warn: 'Revisar',
      missing: 'Falta',
      labels: { Domain: 'Dominio', 'Website IP': 'IP web', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Servidor DNS', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'Necesita atención' },
    summary: {
      aria: 'resumen de la configuración actual',
      hnsInline: 'Nameserver SYNTH HNS',
      delegated: 'DNSSEC + DANE delegado',
      glueRequired: 'Glue requerido',
      externalNameserver: 'Servidor DNS externo',
      dsReady: 'DS listo',
      dsPlaceholder: 'DS pendiente',
      tlsaReady: 'TLSA listo',
      tlsaPlaceholder: 'TLSA pendiente'
    },
    output: {
      parentHns: 'Pon esto en tu cartera HNS / recurso de nombre',
      parentIcann: 'Pon esto en tu registrador / zona padre',
      authoritative: 'Pon esto en tu servidor DNS autoritativo',
      steps: 'Sigue estos pasos',
      verify: 'Verifica con estos comandos',
      web: 'Nota del servidor web',
      integrator: 'JSON de integración',
      server: 'Plantilla de servidor',
      audiences: { parent: 'padre', authoritative: 'autoritativo', server: 'servidor', web: 'web', verify: 'verificar', integrator: 'integrador' }
    },
    faq: {
      title: 'Ayuda',
      setupModeSummary: '¿Qué modo de configuración debería usar?',
      setupModeBody: 'Usa DNS autoritativo delegado para la ruta DANE: crea o elige una zona DNS autoritativa, pon su hostname de nameserver en la cartera o registrador, añade glue si ese hostname está bajo el mismo nombre, activa DNSSEC, publica DS en el padre y publica A/AAAA más TLSA en la zona firmada. Usa HNS SYNTH solo cuando un nombre HNS deba guardar directamente la IP del nameserver autoritativo; SYNTH sigue siendo solo la referencia HNS al nameserver para la misma zona DANE firmada.',
      domainSummary: '¿Qué formato de dominio debo introducir?',
      domainBody: 'Para HNS, introduce un solo nombre en minúsculas que termine en /, por ejemplo dane/. No incluyas etiquetas hijas como www.dane/ ni ningún / interno. Para ICANN, introduce el nombre de dominio DNS, por ejemplo example.com.',
      presetSummary: '¿Qué plantilla debería elegir?',
      presetBody: 'Usa DNS alojado si tu proveedor admite DNSSEC y registros TLSA. Usa archivo de zona genérico si adaptarás registros a BIND, Knot, NSD u otro servidor. Usa PowerDNS si quieres DNS con base de datos o API.',
      splitSummary: '¿Qué va en la cartera o registrador frente al servidor DNS?',
      splitBody: 'La cartera o registrador recibe NS/GLUE o SYNTH, más DS. El servidor DNS autoritativo recibe NS/A/AAAA/TLSA y firma la zona. TLSA es un registro del servidor DNS, no de la cartera.',
      nameserverIpv4Summary: '¿Cuándo necesito una IP de nameserver?',
      nameserverIpv4Body: 'Usa la IPv4 pública del servidor DNS autoritativo. En modo SYNTH HNS se convierte en SYNTH4. En modo delegado solo es GLUE4 del padre cuando el hostname del nameserver vive dentro del mismo nombre o zona.',
      websiteIpv4Summary: '¿Es esta la IP SYNTH o glue?',
      websiteIpv4Body: 'No. La IPv4 del sitio se convierte en el registro A del sitio en el servidor DNS autoritativo. SYNTH y glue sirven para llegar al nameserver; los registros A/AAAA del sitio apuntan los navegadores al servidor web.',
      dnskeySummary: '¿Cuándo pego la DNSKEY?',
      dnskeyBody: 'Pega DNSKEY después de que la zona autoritativa esté firmada con DNSSEC. Esta página la usa para generar el registro DS del padre; no firma la zona ni guarda claves DNSSEC privadas.',
      idnSummary: '¿Puedo usar un dominio internacionalizado?',
      idnBody: 'Sí. La entrada Unicode se convierte a etiquetas ASCII IDNA como xn--bcher-kva.example. Usa la forma A-label en registros DNS, cartera, registrador y configs del servidor.',
      hostedSummary: '¿Qué debe admitir un proveedor DNS alojado?',
      hostedBody: 'El proveedor necesita DNS autoritativo, firma DNSSEC, exportación de DS o DNSKEY y registros TLSA personalizados. Si no puede publicar TLSA en una zona firmada, no puede completar esta ruta DANE.'
    }
  },
  fr: {
    languageName: 'Français',
    languageLabel: 'Langue',
    hero: {
      eyebrow: 'Besoin des enregistrements HNS, registrar, DNSSEC et TLSA ?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'Authentification DNS des entités nommées',
      steps: {
        enter: 'Saisissez ici le nom, le serveur de noms, l’adresse IP du serveur et le certificat.',
        send: 'Envoyez les enregistrements générés à votre wallet HNS ou à votre bureau d’enregistrement.',
        zone: 'Copiez les enregistrements de zone sur le serveur DNS faisant autorité.',
        done: 'Publiez DNSSEC et TLSA afin que le site DANE puisse servir.'
      }
    },
    sections: { domain: '1. Domaine', server: '2. Serveur', dane: '3. DANE' },
    fields: {
      domainType: 'Type de domaine',
      domainTypeHelp: 'HNS signifie wallet/ressource de nom. ICANN signifie panneau du bureau d’enregistrement.',
      setupMode: 'Mode de configuration',
      setupModeHelp: 'Le mode délégué pointe vers un nom de serveur. SYNTH stocke les IPs dans HNS; les deux exigent un DNS signé.',
      domain: 'Domaine',
      domainHelp: 'Exemples : dane/ ou example.com',
      hnsDomainHelp: 'Les noms HNS doivent finir par / et utiliser un seul nom ASCII minuscule : a-z, 0-9, - ou _, avec - et _ seulement au milieu.',
      dnsServerPreset: 'Modèle de serveur DNS',
      dnsServerPresetHelp: 'Cela change l’exemple à copier-coller, pas le sens DNS.',
      nameserverHost: 'Nom du serveur de noms',
      nameserverHostHelp: 'Utilisez les serveurs du fournisseur, ou ns1.votrenom. avec glue si le serveur est sous le même nom.',
      nameserverIpv4: 'IPv4 du serveur de noms',
      nameserverIpv4Help: 'Requise pour SYNTH HNS et les serveurs de noms délégués dans la zone ; facultative pour les serveurs de noms délégués externes.',
      nameserverIpv6: 'IPv6 du serveur de noms (facultatif)',
      nameserverIpv6Help: 'Glue/adresse IPv6 facultative pour le serveur DNS.',
      websiteIpv4: 'IPv4 du site web',
      websiteIpv4Help: 'L’adresse IPv4 publique du serveur web à l’apex du domaine.',
      websiteIpv6: 'IPv6 du site web (facultatif)',
      websiteIpv6Help: 'Adresse IPv6 publique facultative du serveur web à l’apex du domaine.',
      port: 'Port HTTPS',
      portHelp: '443 est la valeur par défaut pour HTTPS normal. Changez-le seulement si le service TLS utilise un autre port.',
      certificate: 'Certificat ou PUBLIC KEY',
      certificateHelp: 'Collez le certificat feuille ou une PUBLIC KEY PEM. Les clés privées ne sont pas nécessaires.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'Après avoir activé la signature DNSSEC sur le serveur DNS, collez ici la DNSKEY de la zone pour générer DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS faisant autorité délégué',
      hnsInline: 'Nameserver SYNTH HNS',
      genericZone: 'Fichier de zone générique',
      hostedDns: 'Panneau DNS hébergé',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'Comment l’obtenir',
      certIntro: 'Collez le certificat public que votre serveur HTTPS sert pour ce nom exact, ou collez la PUBLIC KEY PEM correspondante. Ne collez pas de clé privée.',
      certFetch: 'Si vous avez un accès shell, récupérez le certificat feuille depuis le service actif :',
      certFile: 'Si vous avez déjà un fichier de certificat, collez le bloc de BEGIN CERTIFICATE à END CERTIFICATE. Pour coller seulement la clé publique depuis un certificat, exécutez :',
      dnskeyIntro: 'Activez d’abord la signature DNSSEC sur la zone DNS faisant autorité. Le serveur DNS ou le fournisseur hébergé crée la DNSKEY publique ; cette app utilise cet enregistrement public pour générer le DS parent.',
      dnskeyHosted: 'Dans un panneau DNS hébergé, cherchez les réglages DNSSEC, DS ou DNSKEY. Si le fournisseur donne directement un DS, publiez ce DS dans le wallet ou le bureau d’enregistrement sans coller DNSKEY ici.',
      dnskeyQuery: 'Si votre serveur faisant autorité répond déjà, interrogez la DNSKEY publique et collez la ligne DNSKEY de la zone, généralement la clé de signature avec flags 257 :'
    },
    copy: { nothing: '# Rien généré pour le moment' },
    linode: {
      provisioningTag: 'Provisionnement en un clic',
      cardLink: 'Serveur HNS DANE sur Linode/Akamai',
      cardBody: 'Serveur HNS DANE en un clic pour un seul nom Handshake. Installe Knot DNS faisant autorité avec DNSSEC, TLSA, tableau de bord nginx, vérification locale et commandes de ressource HNS à copier-coller. Ne demande jamais de seed de wallet, de token API Linode, d’identifiants de bureau d’enregistrement ni de données de paiement.',
      deployTitle: 'Déployer sur Linode/Akamai',
      beginHosting: 'Démarrer l’hébergement',
      publishSetup: 'Publier la configuration',
      guide: 'Guide',
      stackScript: 'StackScript'
    },
    status: {
      title: 'État de configuration',
      ok: 'OK',
      warn: 'Vérifier',
      missing: 'Requis',
      labels: { Domain: 'Domaine', 'Website IP': 'IP web', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Serveur de noms', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'À vérifier' },
    summary: {
      aria: 'résumé de la configuration actuelle',
      hnsInline: 'Nameserver SYNTH HNS',
      delegated: 'DNSSEC + DANE délégué',
      glueRequired: 'Glue requise',
      externalNameserver: 'Serveur de noms externe',
      dsReady: 'DS prêt',
      dsPlaceholder: 'DS à compléter',
      tlsaReady: 'TLSA prêt',
      tlsaPlaceholder: 'TLSA à compléter'
    },
    output: {
      parentHns: 'À mettre dans votre wallet HNS / ressource de nom',
      parentIcann: 'À mettre chez votre bureau d’enregistrement / zone parente',
      authoritative: 'À mettre sur votre serveur DNS faisant autorité',
      steps: 'Étapes à suivre',
      verify: 'Vérifier avec ces commandes',
      web: 'Note serveur web',
      integrator: 'JSON intégrateur',
      server: 'Modèle serveur',
      audiences: { parent: 'parent', authoritative: 'autorité', server: 'serveur', web: 'web', verify: 'vérifier', integrator: 'intégrateur' }
    },
    faq: {
      title: 'Aide',
      setupModeSummary: 'Quel mode de configuration choisir ?',
      setupModeBody: 'Utilisez le DNS faisant autorité délégué pour le chemin DANE : créez ou choisissez une zone DNS faisant autorité, mettez son nom de serveur dans le wallet ou chez le registrar, ajoutez de la glue si ce nom est sous le même domaine, activez DNSSEC, publiez le DS chez le parent, puis publiez A/AAAA et TLSA dans la zone signée. Utilisez HNS SYNTH seulement lorsqu’un nom HNS doit stocker directement l’IP du serveur faisant autorité ; SYNTH reste seulement la référence HNS de serveur de noms pour la même zone DANE signée.',
      domainSummary: 'Quel format de domaine saisir ?',
      domainBody: 'Pour HNS, saisissez un seul nom minuscule se terminant par /, par exemple dane/. N’incluez pas de sous-labels comme www.dane/ ni de / interne. Pour ICANN, saisissez le nom de domaine DNS, par exemple example.com.',
      presetSummary: 'Quel modèle choisir ?',
      presetBody: 'Utilisez DNS hébergé si votre fournisseur prend en charge DNSSEC et TLSA. Utilisez le fichier de zone générique pour adapter les enregistrements à BIND, Knot, NSD ou un autre serveur. Utilisez PowerDNS pour un DNS avec base de données ou API.',
      splitSummary: 'Que va dans le wallet ou le bureau d’enregistrement, et que va sur le serveur DNS ?',
      splitBody: 'Le wallet ou le bureau d’enregistrement reçoit NS/GLUE ou SYNTH, plus DS. Le serveur DNS faisant autorité reçoit NS/A/AAAA/TLSA et signe la zone. TLSA est un enregistrement du serveur DNS, pas du wallet.',
      nameserverIpv4Summary: 'Quand faut-il une IP de serveur de noms ?',
      nameserverIpv4Body: 'Utilisez l’adresse IPv4 publique du serveur DNS faisant autorité. En mode SYNTH HNS, elle devient SYNTH4. En mode délégué, elle est seulement GLUE4 côté parent lorsque le nom du serveur de noms se trouve dans le même nom ou la même zone.',
      websiteIpv4Summary: 'Est-ce l’IP SYNTH ou glue ?',
      websiteIpv4Body: 'Non. L’IPv4 du site devient l’enregistrement A du site sur le serveur DNS faisant autorité. SYNTH et glue servent à atteindre le serveur de noms ; les enregistrements A/AAAA du site pointent les navigateurs vers le serveur web.',
      dnskeySummary: 'Quand coller la DNSKEY ?',
      dnskeyBody: 'Collez DNSKEY après la signature DNSSEC de la zone faisant autorité. Cette page l’utilise pour générer le DS parent ; elle ne signe pas la zone et ne stocke pas de clés DNSSEC privées.',
      idnSummary: 'Puis-je utiliser un nom de domaine internationalisé ?',
      idnBody: 'Oui. Le domaine Unicode est converti en A-label ASCII IDNA comme xn--bcher-kva.example. Utilisez cette forme dans les enregistrements DNS, le wallet, le bureau d’enregistrement et les configs serveur.',
      hostedSummary: 'Que doit prendre en charge un fournisseur DNS hébergé ?',
      hostedBody: 'Le fournisseur doit offrir DNS faisant autorité, signature DNSSEC, export DS ou DNSKEY, et enregistrements TLSA personnalisés. S’il ne peut pas publier TLSA dans une zone signée, il ne peut pas compléter ce chemin DANE.'
    }
  },
  de: {
    languageName: 'Deutsch',
    languageLabel: 'Sprache',
    hero: {
      eyebrow: 'Brauchen Sie HNS-, Registrar-, DNSSEC- und TLSA-Einträge?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'DNS-basierte Authentifizierung benannter Entitäten',
      steps: {
        enter: 'Geben Sie hier Namen, Nameserver, Server-IP und Zertifikat ein.',
        send: 'Senden Sie die erzeugten Einträge an Ihr HNS-Wallet oder Ihren Registrar.',
        zone: 'Kopieren Sie die Zoneneinträge auf den autoritativen DNS-Server.',
        done: 'Veröffentlichen Sie DNSSEC und TLSA, damit die DANE-Website ausgeliefert werden kann.'
      }
    },
    sections: { domain: '1. Domain', server: '2. Server', dane: '3. DANE' },
    fields: {
      domainType: 'Domaintyp',
      domainTypeHelp: 'HNS bedeutet Wallet/Namensressource. ICANN bedeutet Registrar-Panel.',
      setupMode: 'Einrichtungsmodus',
      setupModeHelp: 'Delegierter Modus zeigt auf einen Nameserver-Hostnamen. SYNTH speichert IPs in HNS; beide brauchen signiertes DNS.',
      domain: 'Domain',
      domainHelp: 'Beispiele: dane/ oder example.com',
      hnsDomainHelp: 'HNS-Namen müssen mit / enden und einen einzelnen kleingeschriebenen ASCII-Namen verwenden: a-z, 0-9, - oder _, wobei - und _ nur in der Mitte stehen dürfen.',
      dnsServerPreset: 'DNS-Server-Vorlage',
      dnsServerPresetHelp: 'Ändert das Copy-Paste-Beispiel, nicht die DNS-Bedeutung.',
      nameserverHost: 'Nameserver-Hostname',
      nameserverHostHelp: 'Nutzen Sie Provider-Nameserver oder ns1.ihrname. mit Glue, wenn der Nameserver unter demselben Namen liegt.',
      nameserverIpv4: 'Nameserver IPv4',
      nameserverIpv4Help: 'Erforderlich für HNS SYNTH und delegierte Nameserver in der Zone; optional für externe delegierte Nameserver.',
      nameserverIpv6: 'Nameserver IPv6 (optional)',
      nameserverIpv6Help: 'Optionale IPv6-Glue/Adresse für den DNS-Server.',
      websiteIpv4: 'Website IPv4',
      websiteIpv4Help: 'Die öffentliche IPv4-Adresse des Webservers am Domain-Apex.',
      websiteIpv6: 'Website IPv6 (optional)',
      websiteIpv6Help: 'Optionale öffentliche IPv6-Adresse des Webservers am Domain-Apex.',
      port: 'HTTPS-Port',
      portHelp: '443 ist der Standard für normales HTTPS. Ändern Sie ihn nur, wenn der TLS-Dienst einen anderen Port nutzt.',
      certificate: 'Zertifikat oder PUBLIC KEY',
      certificateHelp: 'Fügen Sie das Leaf-Zertifikat oder einen PEM PUBLIC KEY ein. Private Schlüssel werden nicht benötigt.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'Aktivieren Sie DNSSEC-Signierung auf dem DNS-Server und fügen Sie dann die Zone-DNSKEY ein, um DS zu erzeugen.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'Delegiertes autoritatives DNS',
      hnsInline: 'HNS SYNTH-Nameserver',
      genericZone: 'Generische Zonendatei',
      hostedDns: 'Hosted-DNS-Provider-Panel',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'So bekommen Sie das',
      certIntro: 'Fügen Sie das öffentliche Zertifikat ein, das Ihr HTTPS-Server für genau diesen Namen ausliefert, oder den passenden PEM PUBLIC KEY. Fügen Sie keinen privaten Schlüssel ein.',
      certFetch: 'Wenn Sie Shell-Zugriff haben, holen Sie das Leaf-Zertifikat vom Live-Dienst:',
      certFile: 'Wenn Sie bereits eine Zertifikatsdatei haben, fügen Sie den Block von BEGIN CERTIFICATE bis END CERTIFICATE ein. Nur den Public Key extrahieren Sie so:',
      dnskeyIntro: 'Aktivieren Sie zuerst DNSSEC-Signierung auf der autoritativen DNS-Zone. DNS-Server oder Hosted-DNS-Provider erstellt die öffentliche DNSKEY; diese App erzeugt daraus den Parent-DS.',
      dnskeyHosted: 'Suchen Sie im Hosted-DNS-Panel nach DNSSEC-, DS- oder DNSKEY-Einstellungen. Wenn der Provider direkt einen DS liefert, können Sie diesen ohne DNSKEY hier bei Wallet oder Registrar veröffentlichen.',
      dnskeyQuery: 'Wenn Ihr autoritativer Server bereits antwortet, fragen Sie die öffentliche DNSKEY ab und fügen Sie die DNSKEY-Zeile der Zone ein, meist den Key-Signing-Key mit Flags 257:'
    },
    copy: { nothing: '# Noch nichts erzeugt' },
    linode: {
      provisioningTag: 'One-Click-Bereitstellung',
      cardLink: 'HNS-DANE-Server auf Linode/Akamai',
      cardBody: 'One-Click-HNS-DANE-Server für einen einzelnen Handshake-Namen. Installiert autoritatives Knot DNS mit DNSSEC, TLSA, nginx-Dashboard, lokaler Prüfung und Copy-Paste-HNS-Ressourcenbefehlen. Fragt nie nach Wallet-Seeds, Linode-API-Tokens, Registrar-Zugangsdaten oder Zahlungsdaten.',
      deployTitle: 'Auf Linode/Akamai bereitstellen',
      beginHosting: 'Hosting starten',
      publishSetup: 'Einrichtung veröffentlichen',
      guide: 'Anleitung',
      stackScript: 'StackScript'
    },
    status: {
      title: 'Einrichtungsstatus',
      ok: 'OK',
      warn: 'Prüfen',
      missing: 'Fehlt',
      labels: { Domain: 'Domain', 'Website IP': 'Website-IP', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Nameserver', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'Benötigt Aufmerksamkeit' },
    summary: {
      aria: 'Zusammenfassung der aktuellen Einrichtung',
      hnsInline: 'HNS SYNTH-Nameserver',
      delegated: 'Delegiertes DNSSEC + DANE',
      glueRequired: 'Glue erforderlich',
      externalNameserver: 'Externer Nameserver',
      dsReady: 'DS bereit',
      dsPlaceholder: 'DS Platzhalter',
      tlsaReady: 'TLSA bereit',
      tlsaPlaceholder: 'TLSA Platzhalter'
    },
    output: {
      parentHns: 'In Ihr HNS-Wallet / Ihre Namensressource eintragen',
      parentIcann: 'Beim Registrar / in der Parent-Zone eintragen',
      authoritative: 'Auf Ihrem autoritativen DNS-Server eintragen',
      steps: 'Diese Schritte ausführen',
      verify: 'Mit diesen Befehlen prüfen',
      web: 'Webserver-Hinweis',
      integrator: 'Integrator-JSON',
      server: 'Server-Vorlage',
      audiences: { parent: 'parent', authoritative: 'autoritativ', server: 'server', web: 'web', verify: 'prüfen', integrator: 'integrator' }
    },
    faq: {
      title: 'Hilfen',
      setupModeSummary: 'Welchen Einrichtungsmodus soll ich verwenden?',
      setupModeBody: 'Nutzen Sie delegiertes autoritatives DNS für den DANE-Weg: autoritative DNS-Zone erstellen oder wählen, deren Nameserver-Hostnamen in Wallet oder Registrar eintragen, Glue hinzufügen wenn der Hostname unter demselben Namen liegt, DNSSEC aktivieren, DS beim Parent veröffentlichen und A/AAAA plus TLSA in der signierten Zone veröffentlichen. Nutzen Sie HNS SYNTH nur, wenn ein HNS-Name die IP des autoritativen Nameservers direkt speichern soll; SYNTH bleibt nur die HNS-Nameserver-Verweisung für dieselbe signierte DANE-Zone.',
      domainSummary: 'Welches Domainformat soll ich eingeben?',
      domainBody: 'Für HNS geben Sie einen einzelnen kleingeschriebenen Namen mit abschließendem / ein, zum Beispiel dane/. Verwenden Sie keine Child-Labels wie www.dane/ und kein internes /. Für ICANN geben Sie den DNS-Namen ein, zum Beispiel example.com.',
      presetSummary: 'Welche Vorlage sollte ich wählen?',
      presetBody: 'Nutzen Sie Hosted DNS, wenn Ihr Provider DNSSEC und TLSA unterstützt. Nutzen Sie die generische Zonendatei für BIND, Knot, NSD oder andere Server. Nutzen Sie PowerDNS für datenbank- oder API-basiertes DNS.',
      splitSummary: 'Was gehört in Wallet/Registrar und was auf den DNS-Server?',
      splitBody: 'Wallet oder Registrar erhalten NS/GLUE oder SYNTH plus DS. Der autoritative DNS-Server erhält NS/A/AAAA/TLSA und signiert die Zone. TLSA ist ein DNS-Server-Eintrag, kein Wallet-Eintrag.',
      nameserverIpv4Summary: 'Wann brauche ich eine Nameserver-IP?',
      nameserverIpv4Body: 'Verwenden Sie die öffentliche IPv4-Adresse des autoritativen DNS-Servers. Im HNS-SYNTH-Modus wird sie zu SYNTH4. Im delegierten Modus ist sie nur parent-seitiges GLUE4, wenn der Nameserver-Hostname im selben Namen oder in derselben Zone liegt.',
      websiteIpv4Summary: 'Ist das die SYNTH- oder Glue-IP?',
      websiteIpv4Body: 'Nein. Website IPv4 wird zum A-Eintrag der Website auf dem autoritativen DNS-Server. SYNTH und Glue dienen dazu, den Nameserver zu erreichen; Website-A/AAAA-Einträge zeigen Browsern den Webserver.',
      dnskeySummary: 'Wann füge ich DNSKEY ein?',
      dnskeyBody: 'Fügen Sie DNSKEY ein, nachdem die autoritative Zone mit DNSSEC signiert ist. Diese Seite erzeugt daraus den Parent-DS; sie signiert keine Zone und speichert keine privaten DNSSEC-Schlüssel.',
      idnSummary: 'Kann ich einen internationalisierten Domainnamen nutzen?',
      idnBody: 'Ja. Unicode-Domain-Eingaben werden in IDNA-ASCII-A-Labels wie xn--bcher-kva.example umgewandelt. Nutzen Sie die A-Label-Form in DNS, Wallet, Registrar und Serverkonfiguration.',
      hostedSummary: 'Was muss ein Hosted-DNS-Provider unterstützen?',
      hostedBody: 'Der Provider braucht autoritatives DNS-Hosting, DNSSEC-Signierung, DS- oder DNSKEY-Export und benutzerdefinierte TLSA-Einträge. Wenn er TLSA nicht in einer signierten Zone veröffentlichen kann, kann er diesen DANE-Weg nicht abschließen.'
    }
  },
  pt: {
    languageName: 'Português',
    languageLabel: 'Idioma',
    hero: {
      eyebrow: 'Precisa dos registros HNS, registrador, DNSSEC e TLSA?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'Autenticação baseada em DNS de entidades nomeadas',
      steps: {
        enter: 'Informe aqui o nome, o nameserver, o IP do servidor e o certificado.',
        send: 'Envie os registros gerados para sua carteira HNS ou registrador.',
        zone: 'Copie os registros de zona para o servidor DNS autoritativo.',
        done: 'Publique DNSSEC e TLSA para que o site DANE possa servir.'
      }
    },
    sections: { domain: '1. Domínio', server: '2. Servidor', dane: '3. DANE' },
    fields: {
      domainType: 'Tipo de domínio',
      domainTypeHelp: 'HNS significa carteira/recurso de nome. ICANN significa painel do registrador.',
      setupMode: 'Modo de configuração',
      setupModeHelp: 'Modo delegado aponta para um hostname de nameserver. SYNTH guarda IPs no HNS; ambos precisam de DNS assinado.',
      domain: 'Domínio',
      domainHelp: 'Exemplos: dane/ ou example.com',
      hnsDomainHelp: 'Nomes HNS devem terminar com / e usar um unico nome ASCII minusculo: a-z, 0-9, - ou _, com - e _ apenas no meio.',
      dnsServerPreset: 'Preset de servidor DNS',
      dnsServerPresetHelp: 'Isso muda o exemplo de copiar e colar, não o significado DNS.',
      nameserverHost: 'Hostname do nameserver',
      nameserverHostHelp: 'Use nameservers do provedor, ou ns1.seunome. com glue se o nameserver fica sob o mesmo nome.',
      nameserverIpv4: 'IPv4 do nameserver',
      nameserverIpv4Help: 'Obrigatório para SYNTH HNS e nameservers delegados dentro da zona; opcional para nameservers delegados externos.',
      nameserverIpv6: 'IPv6 do nameserver (opcional)',
      nameserverIpv6Help: 'Glue/endereço IPv6 opcional para o servidor DNS.',
      websiteIpv4: 'IPv4 do site',
      websiteIpv4Help: 'O IPv4 público do servidor web no apex do domínio.',
      websiteIpv6: 'IPv6 do site (opcional)',
      websiteIpv6Help: 'IPv6 público opcional do servidor web no apex do domínio.',
      port: 'Porta HTTPS',
      portHelp: '443 é o padrão para HTTPS normal. Altere apenas se o serviço TLS usar outra porta.',
      certificate: 'Certificado ou PUBLIC KEY',
      certificateHelp: 'Cole o certificado folha ou uma PUBLIC KEY em PEM. Chaves privadas não são necessárias.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'Depois de ativar a assinatura DNSSEC no servidor DNS, cole a DNSKEY da zona aqui para gerar DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS autoritativo delegado',
      hnsInline: 'Nameserver SYNTH HNS',
      genericZone: 'Arquivo de zona genérico',
      hostedDns: 'Painel de provedor DNS hospedado',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'Como obter isto',
      certIntro: 'Cole o certificado público que seu servidor HTTPS entrega para este nome exato, ou cole a PUBLIC KEY em PEM correspondente. Não cole uma chave privada.',
      certFetch: 'Se você tem acesso shell, busque o certificado folha no serviço ativo:',
      certFile: 'Se você já tem um arquivo de certificado, cole o bloco de BEGIN CERTIFICATE até END CERTIFICATE. Para colar apenas a chave pública de um certificado, execute:',
      dnskeyIntro: 'Primeiro ative a assinatura DNSSEC na zona DNS autoritativa. O servidor DNS ou provedor hospedado cria a DNSKEY pública; este app usa esse registro público para gerar o DS do parent.',
      dnskeyHosted: 'Em um painel DNS hospedado, procure configurações DNSSEC, DS ou DNSKEY. Se o provedor fornecer um registro DS diretamente, publique esse DS na carteira ou registrador sem colar DNSKEY aqui.',
      dnskeyQuery: 'Se seu servidor autoritativo já responde, consulte a DNSKEY pública e cole a linha DNSKEY da zona, normalmente a chave de assinatura com flags 257:'
    },
    copy: { nothing: '# Nada gerado ainda' },
    linode: {
      provisioningTag: 'Provisionamento com um clique',
      cardLink: 'Servidor HNS DANE na Linode/Akamai',
      cardBody: 'Servidor HNS DANE com um clique para um único nome Handshake. Instala Knot DNS autoritativo com DNSSEC, TLSA, painel nginx, verificação local e comandos de recurso HNS para copiar e colar. Nunca pede seed de carteira, tokens de API da Linode, credenciais de registrador nem dados de pagamento.',
      deployTitle: 'Implantar na Linode/Akamai',
      beginHosting: 'Começar hosting',
      publishSetup: 'Publicar configuração',
      guide: 'Guia',
      stackScript: 'StackScript'
    },
    status: {
      title: 'Status da configuração',
      ok: 'OK',
      warn: 'Verificar',
      missing: 'Necessário',
      labels: { Domain: 'Domínio', 'Website IP': 'IP do site', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Nameserver', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'Precisa de atenção' },
    summary: {
      aria: 'resumo da configuração atual',
      hnsInline: 'Nameserver SYNTH HNS',
      delegated: 'DNSSEC + DANE delegado',
      glueRequired: 'Glue necessário',
      externalNameserver: 'Nameserver externo',
      dsReady: 'DS pronto',
      dsPlaceholder: 'DS pendente',
      tlsaReady: 'TLSA pronto',
      tlsaPlaceholder: 'TLSA pendente'
    },
    output: {
      parentHns: 'Coloque isto na carteira HNS / recurso de nome',
      parentIcann: 'Coloque isto no registrador / zona parent',
      authoritative: 'Coloque isto no servidor DNS autoritativo',
      steps: 'Faça estes passos',
      verify: 'Verifique com estes comandos',
      web: 'Nota do servidor web',
      integrator: 'JSON integrador',
      server: 'Preset de servidor',
      audiences: { parent: 'parent', authoritative: 'autoritativo', server: 'servidor', web: 'web', verify: 'verificar', integrator: 'integrador' }
    },
    faq: {
      title: 'Ajuda',
      setupModeSummary: 'Qual modo de configuração devo usar?',
      setupModeBody: 'Use DNS autoritativo delegado para o caminho DANE: crie ou escolha uma zona DNS autoritativa, coloque o hostname do nameserver na carteira ou registrador, adicione glue se esse hostname fica sob o mesmo nome, ative DNSSEC, publique DS no parent e publique A/AAAA mais TLSA na zona assinada. Use HNS SYNTH apenas quando um nome HNS deve armazenar diretamente o IP do nameserver autoritativo; SYNTH ainda é só a referência HNS de nameserver para a mesma zona DANE assinada.',
      domainSummary: 'Qual formato de domínio devo informar?',
      domainBody: 'Para HNS, informe um único nome em minúsculas terminando com /, por exemplo dane/. Não inclua labels filhas como www.dane/ nem qualquer / interno. Para ICANN, informe o nome de domínio DNS, por exemplo example.com.',
      presetSummary: 'Qual preset devo escolher?',
      presetBody: 'Use DNS hospedado se seu provedor suporta DNSSEC e TLSA. Use arquivo de zona genérico ao adaptar registros para BIND, Knot, NSD ou outro servidor. Use PowerDNS se quiser DNS com banco de dados ou API.',
      splitSummary: 'O que vai na carteira/registrador versus no servidor DNS?',
      splitBody: 'A carteira ou registrador recebe NS/GLUE ou SYNTH, mais DS. O servidor DNS autoritativo recebe NS/A/AAAA/TLSA e assina a zona. TLSA é um registro do servidor DNS, não da carteira.',
      nameserverIpv4Summary: 'Quando preciso de um IP de nameserver?',
      nameserverIpv4Body: 'Use o IPv4 público do servidor DNS autoritativo. No modo SYNTH HNS ele vira SYNTH4. No modo delegado ele só é GLUE4 no parent quando o hostname do nameserver fica dentro do mesmo nome ou zona.',
      websiteIpv4Summary: 'Este é o IP SYNTH ou glue?',
      websiteIpv4Body: 'Não. Website IPv4 vira o registro A do site no servidor DNS autoritativo. SYNTH e glue servem para chegar ao nameserver; registros A/AAAA do site apontam navegadores para o servidor web.',
      dnskeySummary: 'Quando colo a DNSKEY?',
      dnskeyBody: 'Cole DNSKEY depois que a zona autoritativa estiver assinada com DNSSEC. Esta página usa isso para gerar o registro DS do parent; ela não assina a zona nem armazena chaves DNSSEC privadas.',
      idnSummary: 'Posso usar um domínio internacionalizado?',
      idnBody: 'Sim. Entrada Unicode é convertida para A-labels ASCII IDNA como xn--bcher-kva.example. Use a forma A-label em DNS, carteira, registrador e configs do servidor.',
      hostedSummary: 'O que um provedor DNS hospedado precisa suportar?',
      hostedBody: 'O provedor precisa de DNS autoritativo, assinatura DNSSEC, exportação de DS ou DNSKEY e registros TLSA personalizados. Se não puder publicar TLSA em uma zona assinada, ele não completa este caminho DANE.'
    }
  },
  ja: {
    languageName: '日本語',
    languageLabel: '言語',
    hero: {
      eyebrow: 'HNS、レジストラ、DNSSEC、TLSA のレコードが必要ですか?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'DNS ベースの名前付きエンティティ認証',
      steps: {
        enter: 'ここに名前、ネームサーバー、サーバー IP、証明書を入力します。',
        send: '生成されたレコードを HNS ウォレットまたはレジストラへ送信します。',
        zone: 'ゾーンレコードを権威 DNS サーバーへコピーします。',
        done: 'DNSSEC と TLSA を公開して DANE Web サイトを配信できるようにします。'
      }
    },
    sections: { domain: '1. ドメイン', server: '2. サーバー', dane: '3. DANE' },
    fields: {
      domainType: 'ドメイン種別',
      domainTypeHelp: 'HNS はウォレット/名前リソース、ICANN はレジストラ画面を意味します。',
      setupMode: '設定モード',
      setupModeHelp: '委任モードはネームサーバー名を指します。SYNTH は HNS に IP を保存します。どちらも署名済み権威 DNS が必要です。',
      domain: 'ドメイン',
      domainHelp: '例: dane/ または example.com',
      hnsDomainHelp: 'HNS 名は / で終わり、1 つの小文字 ASCII 名だけを使います: a-z、0-9、-、_。- と _ は中間のみです。',
      dnsServerPreset: 'DNS サーバープリセット',
      dnsServerPresetHelp: 'コピー用のサーバー例だけを変えます。DNS の意味は変わりません。',
      nameserverHost: 'ネームサーバー名',
      nameserverHostHelp: 'プロバイダーのネームサーバー、または同じ名前の下なら glue 付きで ns1.yourname. を使います。',
      nameserverIpv4: 'ネームサーバー IPv4',
      nameserverIpv4Help: 'HNS SYNTH とゾーン内の委任ネームサーバーでは必須です。外部の委任ネームサーバーでは任意です。',
      nameserverIpv6: 'ネームサーバー IPv6 (任意)',
      nameserverIpv6Help: 'DNS サーバー用の任意の IPv6 glue/アドレスです。',
      websiteIpv4: 'Web サイト IPv4',
      websiteIpv4Help: 'ドメイン apex の Web サーバー公開 IPv4 アドレスです。',
      websiteIpv6: 'Web サイト IPv6 (任意)',
      websiteIpv6Help: 'ドメイン apex の Web サーバー公開 IPv6 アドレスです。',
      port: 'HTTPS ポート',
      portHelp: '通常の HTTPS では 443 が既定値です。TLS サービスが別のポートを使う場合だけ変更します。',
      certificate: '証明書または PUBLIC KEY',
      certificateHelp: 'リーフ証明書または PEM PUBLIC KEY を貼り付けます。秘密鍵は不要です。',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'DNS サーバーで DNSSEC 署名を有効にした後、ゾーンの DNSKEY をここに貼り付けて DS を生成します。'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: '委任された権威 DNS',
      hnsInline: 'HNS SYNTH ネームサーバー',
      genericZone: '汎用ゾーンファイル',
      hostedDns: 'ホスト型 DNS プロバイダー画面',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: '取得方法',
      certIntro: 'この名前に対して HTTPS サーバーが返す公開証明書、または対応する PEM PUBLIC KEY を貼り付けます。秘密鍵は貼り付けないでください。',
      certFetch: 'シェルアクセスがある場合は、稼働中のサービスからリーフ証明書を取得します:',
      certFile: '証明書ファイルがある場合は、BEGIN CERTIFICATE から END CERTIFICATE までのブロックを貼り付けます。証明書から公開鍵だけを取り出すには:',
      dnskeyIntro: 'まず権威 DNS ゾーンで DNSSEC 署名を有効にします。DNS サーバーまたはホスト型 DNS プロバイダーが公開 DNSKEY を作成し、このアプリはそれを使って親側 DS を生成します。',
      dnskeyHosted: 'ホスト型 DNS 画面では DNSSEC、DS、DNSKEY の設定を探します。プロバイダーが DS を直接表示する場合は、ここに DNSKEY を貼らずにその DS をウォレットまたはレジストラに公開できます。',
      dnskeyQuery: '権威サーバーがすでに応答している場合は、公開 DNSKEY を問い合わせ、通常 flags 257 のキー署名鍵の DNSKEY 行を貼り付けます:'
    },
    copy: { nothing: '# まだ生成されていません' },
    linode: {
      provisioningTag: 'ワンクリックプロビジョニング',
      cardLink: 'Linode/Akamai 上の HNS DANE サーバー',
      cardBody: '単一の Handshake 名向けのワンクリック HNS DANE サーバーです。Knot DNS の権威 DNSSEC、TLSA、nginx ダッシュボード、ローカル検証、コピーして使える HNS リソースコマンドをインストールします。ウォレット seed、Linode API トークン、レジストラ認証情報、支払い情報は要求しません。',
      deployTitle: 'Linode/Akamai にデプロイ',
      beginHosting: 'ホスティングを開始',
      publishSetup: '設定を公開',
      guide: 'ガイド',
      stackScript: 'StackScript'
    },
    status: {
      title: '設定状態',
      ok: 'OK',
      warn: '確認',
      missing: '必要',
      labels: { Domain: 'ドメイン', 'Website IP': 'Web IP', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'ネームサーバー', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: '確認が必要' },
    summary: {
      aria: '現在の設定概要',
      hnsInline: 'HNS SYNTH ネームサーバー',
      delegated: '委任 DNSSEC + DANE',
      glueRequired: 'Glue が必要',
      externalNameserver: '外部ネームサーバー',
      dsReady: 'DS 生成済み',
      dsPlaceholder: 'DS 未入力',
      tlsaReady: 'TLSA 生成済み',
      tlsaPlaceholder: 'TLSA 未入力'
    },
    output: {
      parentHns: 'HNS ウォレット / 名前リソースに入れる内容',
      parentIcann: 'レジストラ / 親ゾーンに入れる内容',
      authoritative: '権威 DNS サーバーに入れる内容',
      steps: '手順',
      verify: 'このコマンドで確認',
      web: 'Web サーバーメモ',
      integrator: 'Integrator JSON',
      server: 'サーバープリセット',
      audiences: { parent: '親', authoritative: '権威', server: 'サーバー', web: 'web', verify: '確認', integrator: 'integrator' }
    },
    faq: {
      title: 'ヘルプ',
      setupModeSummary: 'どの設定モードを使いますか?',
      setupModeBody: 'DANE 設定パスには委任された権威 DNS を使います。権威 DNS ゾーンを作成または選択し、そのネームサーバー名をウォレットまたはレジストラに入れ、同じ名前の下にある場合は glue を追加し、DNSSEC を有効化し、親側に DS を公開し、署名済みゾーンに A/AAAA と TLSA を公開します。HNS SYNTH は、HNS 名に権威ネームサーバー IP を直接保存したい場合だけ使います。SYNTH も同じ署名済み DANE ゾーンへの HNS ネームサーバー参照にすぎません。',
      domainSummary: 'どのドメイン形式を入力しますか?',
      domainBody: 'HNS では、dane/ のように / で終わる 1 つの小文字名を入力します。www.dane/ のような子ラベルや内部の / は含めません。ICANN では example.com のような DNS ドメイン名を入力します。',
      presetSummary: 'どのプリセットを選ぶべきですか?',
      presetBody: 'プロバイダーが DNSSEC と TLSA をサポートしているならホスト型 DNS を使います。BIND、Knot、NSD などへ合わせるなら汎用ゾーンファイルを使います。DB/API ベースの DNS なら PowerDNS を使います。',
      splitSummary: 'ウォレット/レジストラと DNS サーバーには何を入れますか?',
      splitBody: 'ウォレットまたはレジストラには NS/GLUE または SYNTH と DS を入れます。権威 DNS サーバーには NS/A/AAAA/TLSA を入れてゾーンを署名します。TLSA はウォレットではなく DNS サーバー側のレコードです。',
      nameserverIpv4Summary: 'ネームサーバー IP はいつ必要ですか?',
      nameserverIpv4Body: '権威 DNS サーバーの公開 IPv4 アドレスを使います。HNS SYNTH モードでは SYNTH4 になります。委任モードでは、ネームサーバー名が同じ名前またはゾーン内にある場合だけ親側の GLUE4 になります。',
      websiteIpv4Summary: 'これは SYNTH または glue の IP ですか?',
      websiteIpv4Body: 'いいえ。Web サイト IPv4 は権威 DNS サーバー上のサイトの A レコードになります。SYNTH と glue はネームサーバーへ到達するためのものです。サイトの A/AAAA レコードはブラウザーを Web サーバーへ向けます。',
      dnskeySummary: 'DNSKEY はいつ貼り付けますか?',
      dnskeyBody: '権威ゾーンが DNSSEC 署名された後に DNSKEY を貼ります。このページは親側 DS を生成するだけで、ゾーン署名や秘密 DNSSEC 鍵の保存はしません。',
      idnSummary: '国際化ドメイン名は使えますか?',
      idnBody: 'はい。Unicode ドメイン入力は xn--bcher-kva.example のような IDNA ASCII A-label に変換されます。DNS レコード、ウォレット、レジストラ、サーバー設定では A-label 形式を使います。',
      hostedSummary: 'ホスト型 DNS プロバイダーに必要な機能は?',
      hostedBody: '権威 DNS ホスティング、DNSSEC 署名、DS または DNSKEY のエクスポート、カスタム TLSA レコードが必要です。署名済みゾーンに TLSA を公開できないプロバイダーでは、この DANE 経路は完了できません。'
    }
  },
  ar: {
    languageName: 'العربية',
    languageLabel: 'اللغة',
    hero: {
      eyebrow: 'هل تحتاج إلى سجلات HNS والمحضر و DNSSEC و TLSA؟',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'المصادقة القائمة على DNS للكيانات المسماة',
      steps: {
        enter: 'أدخل الاسم، وخادم الأسماء، وعنوان IP للخادم، والشهادة هنا.',
        send: 'أرسل السجلات المولدة إلى محفظة HNS أو المحضر.',
        zone: 'انسخ سجلات المنطقة إلى خادم DNS السلطوي.',
        done: 'انشر DNSSEC و TLSA حتى يعمل موقع DANE.'
      }
    },
    sections: { domain: '1. النطاق', server: '2. الخادم', dane: '3. DANE' },
    fields: {
      domainType: 'نوع النطاق',
      domainTypeHelp: 'HNS يعني محفظة/مورد اسم. ICANN يعني لوحة المحضر.',
      setupMode: 'وضع الإعداد',
      setupModeHelp: 'الوضع المفوض يشير إلى اسم مضيف خادم أسماء. SYNTH يخزن عناوين IP لخوادم الأسماء في HNS؛ وكلاهما يحتاج إلى DNS سلطوي موقع.',
      domain: 'النطاق',
      domainHelp: 'أمثلة: dane/ أو example.com',
      hnsDomainHelp: 'يجب أن تنتهي أسماء HNS بـ / وأن تستخدم اسما ASCII واحدا بحروف صغيرة: a-z و 0-9 و - أو _، على أن يكون - و _ في الوسط فقط.',
      dnsServerPreset: 'إعداد خادم DNS',
      dnsServerPresetHelp: 'هذا يغير مثال الخادم القابل للنسخ فقط، وليس معنى DNS.',
      nameserverHost: 'اسم مضيف خادم الأسماء',
      nameserverHostHelp: 'استخدم خوادم أسماء المزود، أو استخدم ns1.yourname. مع glue إذا كان خادم الأسماء تحت الاسم نفسه.',
      nameserverIpv4: 'IPv4 لخادم الأسماء',
      nameserverIpv4Help: 'مطلوب لـ HNS SYNTH وخوادم الأسماء المفوضة داخل المنطقة؛ اختياري لخوادم الأسماء المفوضة الخارجية.',
      nameserverIpv6: 'IPv6 لخادم الأسماء (اختياري)',
      nameserverIpv6Help: 'عنوان/glue اختياري عبر IPv6 لخادم DNS.',
      websiteIpv4: 'IPv4 للموقع',
      websiteIpv4Help: 'عنوان IPv4 العام لخادم الويب عند apex النطاق.',
      websiteIpv6: 'IPv6 للموقع (اختياري)',
      websiteIpv6Help: 'عنوان IPv6 عام اختياري لخادم الويب عند apex النطاق.',
      port: 'منفذ HTTPS',
      portHelp: '443 هو الافتراضي لـ HTTPS العادي. غيّره فقط إذا كانت خدمة TLS تستخدم منفذا آخر.',
      certificate: 'شهادة TLSA أو PUBLIC KEY',
      certificateHelp: 'الصق شهادة leaf أو PEM PUBLIC KEY هنا لتوليد سجل TLSA. لا حاجة إلى المفاتيح الخاصة.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'بعد تفعيل توقيع DNSSEC على خادم DNS، الصق DNSKEY الخاص بالمنطقة هنا لتوليد DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS سلطوي مفوض',
      hnsInline: 'خادم أسماء HNS SYNTH',
      genericZone: 'ملف منطقة عام',
      hostedDns: 'لوحة مزود DNS مستضاف',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'كيفية الحصول على هذا',
      certIntro: 'هذا هو حقل مصدر TLSA. الصق الشهادة العامة التي يقدمها خادم TLS لهذا الاسم والمنفذ والبروتوكول واسم SNI تحديدا، أو الصق PEM PUBLIC KEY المطابق. لا تلصق مفتاحا خاصا.',
      certFetch: 'إذا كان لديك وصول shell، اجلب شهادة leaf من الخدمة الحية:',
      certFile: 'إذا كان لديك ملف شهادة، الصق الكتلة من BEGIN CERTIFICATE إلى END CERTIFICATE. للصق المفتاح العام فقط من ملف شهادة، شغّل:',
      dnskeyIntro: 'فعّل أولا توقيع DNSSEC على منطقة الطفل السلطوية الدقيقة التي سيتم تفويضها. خادم DNS أو مزود DNS المستضاف ينشئ DNSKEY العام؛ يستخدم هذا التطبيق ذلك السجل العام لتوليد سجل DS في جهة الأصل.',
      dnskeyHosted: 'في لوحة DNS مستضاف، ابحث عن إعدادات DNSSEC أو DS أو DNSKEY. إذا أعطاك المزود سجل DS مباشرة، يمكنك نشر DS في المحفظة أو المحضر دون لصق DNSKEY هنا.',
      dnskeyQuery: 'إذا كان الخادم السلطوي يجيب بالفعل، استعلم عن DNSKEY العام والصق سطر DNSKEY لهذه المنطقة، عادة مفتاح التوقيع flags 257:'
    },
    copy: { nothing: '# لم يتم توليد شيء بعد' },
    linode: {
      provisioningTag: 'توفير بنقرة واحدة',
      cardLink: 'خادم HNS DANE على Linode/Akamai',
      cardBody: 'خادم HNS DANE بنقرة واحدة لاسم Handshake واحد. يثبت Knot DNS سلطويا مع DNSSEC و TLSA ولوحة nginx والتحقق المحلي وأوامر موارد HNS قابلة للنسخ واللصق. لا يطلب أبدا عبارات seed للمحفظة أو رموز Linode API أو بيانات اعتماد المحضر أو بيانات الدفع.',
      deployTitle: 'النشر على Linode/Akamai',
      beginHosting: 'بدء الاستضافة',
      publishSetup: 'نشر الإعداد',
      guide: 'الدليل',
      stackScript: 'StackScript'
    },
    status: {
      title: 'حالة الإعداد',
      ok: 'OK',
      warn: 'تحقق',
      missing: 'مطلوب',
      labels: { Domain: 'النطاق', 'Website IP': 'IP الموقع', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'خادم الأسماء', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'يحتاج إلى انتباه' },
    summary: {
      aria: 'ملخص الإعداد الحالي',
      hnsInline: 'خادم أسماء HNS SYNTH',
      delegated: 'DNSSEC + DANE مفوض',
      glueRequired: 'Glue مطلوب',
      externalNameserver: 'خادم أسماء خارجي',
      dsReady: 'DS جاهز',
      dsPlaceholder: 'DS غير مكتمل',
      tlsaReady: 'TLSA جاهز',
      tlsaPlaceholder: 'TLSA غير مكتمل'
    },
    output: {
      parentHns: 'ضع هذا في محفظة HNS / مورد الاسم',
      parentIcann: 'ضع هذا عند المحضر / منطقة الأصل',
      authoritative: 'ضع هذا على خادم DNS السلطوي',
      steps: 'نفذ هذه الخطوات',
      verify: 'تحقق بهذه الأوامر',
      web: 'ملاحظة خادم الويب',
      integrator: 'JSON التكامل',
      server: 'إعداد الخادم',
      audiences: { parent: 'الأصل', authoritative: 'سلطوي', server: 'الخادم', web: 'web', verify: 'تحقق', integrator: 'integrator' }
    },
    faq: {
      title: 'نصائح المساعدة',
      setupModeSummary: 'أي وضع إعداد يجب أن أستخدم؟',
      setupModeBody: 'استخدم DNS السلطوي المفوض لمسار إعداد DANE: أنشئ أو اختر منطقة DNS سلطوية، ضع اسم مضيف خادم الأسماء في المحفظة أو المحضر، أضف glue إذا كان اسم المضيف تحت الاسم نفسه، فعّل DNSSEC، انشر DS عند الأصل، وانشر A/AAAA و TLSA في المنطقة الموقعة. استخدم HNS SYNTH فقط عندما يجب أن يخزن اسم HNS عنوان IP لخادم الأسماء السلطوي مباشرة؛ SYNTH يبقى إحالة HNS لخادم الأسماء لنفس منطقة DANE الموقعة.',
      domainSummary: 'ما تنسيق النطاق الذي أدخله؟',
      domainBody: 'بالنسبة إلى HNS، أدخل اسما واحدا بحروف صغيرة ينتهي بـ /، مثل dane/. لا تدخل labels فرعية مثل www.dane/ ولا أي / داخلي. بالنسبة إلى ICANN، أدخل اسم نطاق DNS مثل example.com.',
      presetSummary: 'أي إعداد أختار؟',
      presetBody: 'استخدم DNS مستضافا إذا كان المزود يدعم DNSSEC وسجلات TLSA. استخدم ملف منطقة عام عند تكييف السجلات مع BIND أو Knot أو NSD أو خادم آخر. استخدم PowerDNS إذا أردت DNS بقاعدة بيانات أو API.',
      splitSummary: 'ما الذي يوضع في المحفظة أو المحضر مقابل خادم DNS؟',
      splitBody: 'المحفظة أو المحضر يحصل على NS/GLUE أو SYNTH، بالإضافة إلى DS. خادم DNS السلطوي يحصل على NS/A/AAAA/TLSA ويوقع المنطقة. TLSA سجل على خادم DNS وليس سجل محفظة.',
      nameserverIpv4Summary: 'متى أحتاج إلى IP لخادم الأسماء؟',
      nameserverIpv4Body: 'استخدم عنوان IPv4 العام لخادم DNS السلطوي. في وضع HNS SYNTH يصبح SYNTH4. في الوضع المفوض يكون GLUE4 في جهة الأصل فقط عندما يكون اسم مضيف خادم الأسماء داخل الاسم أو المنطقة نفسها.',
      websiteIpv4Summary: 'هل هذا هو IP الخاص بـ SYNTH أو glue؟',
      websiteIpv4Body: 'لا. IPv4 للموقع يصبح سجل A للموقع على خادم DNS السلطوي. SYNTH و glue مخصصان للوصول إلى خادم الأسماء؛ سجلات A/AAAA للموقع توجه المتصفحات إلى خادم الويب.',
      dnskeySummary: 'متى ألصق DNSKEY؟',
      dnskeyBody: 'الصق DNSKEY بعد توقيع منطقة الطفل السلطوية الدقيقة بـ DNSSEC. تستخدم هذه الصفحة ذلك لتوليد سجل DS في جهة الأصل؛ ولا توقع المنطقة ولا تخزن مفاتيح DNSSEC الخاصة. تحقق من السلسلة بعد نشر DS.',
      idnSummary: 'هل يمكنني استخدام اسم نطاق دولي؟',
      idnBody: 'نعم. يتم تحويل إدخال النطاق Unicode إلى IDNA ASCII A-labels مثل xn--bcher-kva.example. استخدم صيغة A-label في سجلات DNS وحقول المحفظة والمحضر وإعدادات الخادم.',
      hostedSummary: 'ما الذي يجب أن يدعمه مزود DNS مستضاف؟',
      hostedBody: 'يحتاج المزود إلى استضافة DNS سلطوية، وتوقيع DNSSEC، وتصدير DS أو DNSKEY، وسجلات TLSA مخصصة، وتجديد التواقيع، وسجلات نفي مصادق عليها، وتشخيصات كافية لتأكيد تحقق DNSSEC. إذا لم يستطع نشر TLSA في منطقة موقعة، فلن يكتمل مسار DANE هذا.'
    }
  },
  fa: {
    languageName: 'فارسی',
    languageLabel: 'زبان',
    hero: {
      eyebrow: 'به رکوردهای HNS، رجیسترار، DNSSEC و TLSA نیاز دارید؟',
      title: 'تولیدکننده رکورد DANE',
      standard: 'احراز هویت موجودیت‌های نام‌گذاری‌شده بر پایه DNS',
      steps: {
        enter: 'نام، nameserver، IP سرور و گواهی را اینجا وارد کنید.',
        send: 'رکوردهای تولیدشده را به کیف پول HNS یا رجیسترار بفرستید.',
        zone: 'رکوردهای zone را روی سرور DNS مقتدر کپی کنید.',
        done: 'DNSSEC و TLSA را منتشر کنید تا سایت DANE آماده سرویس‌دهی شود.'
      }
    },
    sections: { domain: '1. دامنه', server: '2. سرور', dane: '3. DANE' },
    fields: {
      domainType: 'نوع دامنه',
      domainTypeHelp: 'HNS یعنی کیف پول/منبع نام. ICANN یعنی پنل رجیسترار.',
      setupMode: 'حالت راه‌اندازی',
      setupModeHelp: 'حالت delegated به hostname یک nameserver اشاره می‌کند. SYNTH آدرس‌های IP nameserver را در HNS ذخیره می‌کند؛ هر دو به DNS مقتدر امضاشده نیاز دارند.',
      domain: 'دامنه',
      domainHelp: 'نمونه‌ها: dane/ یا example.com',
      hnsDomainHelp: 'نام‌های HNS باید با / تمام شوند و فقط یک نام ASCII با حروف کوچک داشته باشند: a-z، 0-9، - یا _، و - و _ فقط در میانه باشند.',
      dnsServerPreset: 'preset سرور DNS',
      dnsServerPresetHelp: 'این فقط نمونه قابل کپی سرور را عوض می‌کند، نه معنی DNS را.',
      nameserverHost: 'hostname nameserver',
      nameserverHostHelp: 'از nameserverهای ارائه‌دهنده استفاده کنید، یا اگر nameserver زیر همان نام است از ns1.yourname. همراه glue استفاده کنید.',
      nameserverIpv4: 'IPv4 nameserver',
      nameserverIpv4Help: 'برای HNS SYNTH و nameserverهای delegated داخل zone لازم است؛ برای nameserverهای delegated خارجی اختیاری است.',
      nameserverIpv6: 'IPv6 nameserver (اختیاری)',
      nameserverIpv6Help: 'آدرس/glue اختیاری IPv6 برای سرور DNS.',
      websiteIpv4: 'IPv4 وب‌سایت',
      websiteIpv4Help: 'آدرس IPv4 عمومی سرور وب در apex دامنه.',
      websiteIpv6: 'IPv6 وب‌سایت (اختیاری)',
      websiteIpv6Help: 'آدرس IPv6 عمومی اختیاری سرور وب در apex دامنه.',
      port: 'پورت HTTPS',
      portHelp: '443 مقدار پیش‌فرض HTTPS معمولی است. فقط اگر سرویس TLS از پورت دیگری استفاده می‌کند آن را تغییر دهید.',
      certificate: 'گواهی TLSA یا PUBLIC KEY',
      certificateHelp: 'برای تولید رکورد TLSA، گواهی leaf یا PEM PUBLIC KEY را اینجا بچسبانید. کلید خصوصی لازم نیست.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'بعد از فعال کردن امضای DNSSEC روی سرور DNS، DNSKEY مربوط به zone را اینجا بچسبانید تا DS تولید شود.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS مقتدر delegated',
      hnsInline: 'HNS SYNTH nameserver',
      genericZone: 'فایل zone عمومی',
      hostedDns: 'پنل DNS میزبانی‌شده',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'چطور به دست آورم',
      certIntro: 'این فیلد منبع TLSA است. گواهی عمومی‌ای را که سرور TLS برای همین hostname، پورت، پروتکل و نام SNI ارائه می‌کند بچسبانید، یا PEM PUBLIC KEY مطابق را بچسبانید. کلید خصوصی را وارد نکنید.',
      certFetch: 'اگر دسترسی shell دارید، گواهی leaf را از سرویس زنده بگیرید:',
      certFile: 'اگر فایل گواهی دارید، بلوک BEGIN CERTIFICATE تا END CERTIFICATE را بچسبانید. برای چسباندن فقط کلید عمومی از فایل گواهی، اجرا کنید:',
      dnskeyIntro: 'ابتدا امضای DNSSEC را روی همان child zone مقتدری که delegated می‌شود فعال کنید. سرور DNS یا ارائه‌دهنده DNS میزبانی‌شده DNSKEY عمومی را می‌سازد؛ این برنامه از آن رکورد عمومی برای تولید DS در parent استفاده می‌کند.',
      dnskeyHosted: 'در پنل DNS میزبانی‌شده دنبال تنظیمات DNSSEC، DS یا DNSKEY بگردید. اگر ارائه‌دهنده مستقیما DS می‌دهد، می‌توانید همان DS را در کیف پول یا رجیسترار منتشر کنید و لازم نیست DNSKEY را اینجا بچسبانید.',
      dnskeyQuery: 'اگر سرور مقتدر شما همین حالا پاسخ می‌دهد، DNSKEY عمومی را query کنید و خط DNSKEY این zone را بچسبانید؛ معمولا key-signing key با flags 257:'
    },
    copy: { nothing: '# هنوز چیزی تولید نشده است' },
    linode: {
      provisioningTag: 'راه‌اندازی با یک کلیک',
      cardLink: 'سرور HNS DANE روی Linode/Akamai',
      cardBody: 'سرور HNS DANE با یک کلیک برای یک نام Handshake. Knot DNS مقتدر همراه DNSSEC، TLSA، داشبورد nginx، بررسی محلی و دستورهای منبع HNS قابل کپی و چسباندن را نصب می‌کند. هرگز seed کیف پول، توکن API لینود، اطلاعات ورود رجیسترار یا داده پرداخت نمی‌خواهد.',
      deployTitle: 'استقرار روی Linode/Akamai',
      beginHosting: 'شروع میزبانی',
      publishSetup: 'انتشار تنظیمات',
      guide: 'راهنما',
      stackScript: 'StackScript'
    },
    status: {
      title: 'وضعیت راه‌اندازی',
      ok: 'OK',
      warn: 'بررسی',
      missing: 'لازم',
      labels: { Domain: 'دامنه', 'Website IP': 'IP وب‌سایت', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Nameserver', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'نیازمند توجه' },
    summary: {
      aria: 'خلاصه تنظیمات فعلی',
      hnsInline: 'HNS SYNTH nameserver',
      delegated: 'DNSSEC + DANE delegated',
      glueRequired: 'Glue لازم است',
      externalNameserver: 'Nameserver خارجی',
      dsReady: 'DS آماده است',
      dsPlaceholder: 'DS ناقص است',
      tlsaReady: 'TLSA آماده است',
      tlsaPlaceholder: 'TLSA ناقص است'
    },
    output: {
      parentHns: 'این را در کیف پول HNS / منبع نام بگذارید',
      parentIcann: 'این را در رجیسترار / parent zone بگذارید',
      authoritative: 'این را روی سرور DNS مقتدر بگذارید',
      steps: 'این مراحل را انجام دهید',
      verify: 'با این دستورها بررسی کنید',
      web: 'یادداشت سرور وب',
      integrator: 'Integrator JSON',
      server: 'Preset سرور',
      audiences: { parent: 'parent', authoritative: 'مقتدر', server: 'سرور', web: 'web', verify: 'بررسی', integrator: 'integrator' }
    },
    faq: {
      title: 'راهنما',
      setupModeSummary: 'کدام حالت راه‌اندازی را استفاده کنم؟',
      setupModeBody: 'برای مسیر راه‌اندازی DANE از DNS مقتدر delegated استفاده کنید: یک zone مقتدر بسازید یا انتخاب کنید، hostname nameserver آن را در کیف پول یا رجیسترار قرار دهید، اگر آن hostname زیر همان نام است glue اضافه کنید، DNSSEC را فعال کنید، DS را در parent منتشر کنید، و A/AAAA به‌همراه TLSA را در zone امضاشده منتشر کنید. HNS SYNTH را فقط وقتی استفاده کنید که یک نام HNS باید IP nameserver مقتدر را مستقیم ذخیره کند؛ SYNTH همچنان فقط ارجاع HNS به nameserver برای همان zone امضاشده DANE است.',
      domainSummary: 'چه قالب دامنه‌ای وارد کنم؟',
      domainBody: 'برای HNS، یک نام کوچک که با / تمام می‌شود وارد کنید، مثلا dane/. label فرزند مثل www.dane/ یا / داخلی وارد نکنید. برای ICANN، نام دامنه DNS را وارد کنید، مثلا example.com.',
      presetSummary: 'کدام preset را انتخاب کنم؟',
      presetBody: 'اگر ارائه‌دهنده شما DNSSEC و رکوردهای TLSA را پشتیبانی می‌کند از Hosted DNS استفاده کنید. اگر رکوردها را برای BIND، Knot، NSD یا سرور دیگر تطبیق می‌دهید از Generic zone file استفاده کنید. اگر DNS مبتنی بر پایگاه داده یا API می‌خواهید از PowerDNS استفاده کنید.',
      splitSummary: 'چه چیزی در کیف پول/رجیسترار و چه چیزی روی سرور DNS قرار می‌گیرد؟',
      splitBody: 'کیف پول یا رجیسترار NS/GLUE یا SYNTH به‌همراه DS را می‌گیرد. سرور DNS مقتدر NS/A/AAAA/TLSA را می‌گیرد و zone را امضا می‌کند. TLSA رکورد سرور DNS است، نه رکورد کیف پول.',
      nameserverIpv4Summary: 'چه زمانی به IP nameserver نیاز دارم؟',
      nameserverIpv4Body: 'از آدرس IPv4 عمومی سرور DNS مقتدر استفاده کنید. در حالت HNS SYNTH این آدرس به SYNTH4 تبدیل می‌شود. در حالت delegated فقط وقتی hostname nameserver داخل همان نام یا zone است، GLUE4 در parent می‌شود.',
      websiteIpv4Summary: 'آیا این IP مربوط به SYNTH یا glue است؟',
      websiteIpv4Body: 'نه. IPv4 وب‌سایت به رکورد A سایت روی سرور DNS مقتدر تبدیل می‌شود. SYNTH و glue برای رسیدن به nameserver هستند؛ رکوردهای A/AAAA وب‌سایت مرورگرها را به سرور وب هدایت می‌کنند.',
      dnskeySummary: 'چه زمانی DNSKEY را بچسبانم؟',
      dnskeyBody: 'بعد از اینکه child zone مقتدر با DNSSEC امضا شد DNSKEY را بچسبانید. این صفحه از آن برای تولید DS در parent استفاده می‌کند؛ zone را امضا نمی‌کند و کلیدهای خصوصی DNSSEC را ذخیره نمی‌کند. بعد از انتشار DS زنجیره را اعتبارسنجی کنید.',
      idnSummary: 'آیا می‌توانم از نام دامنه بین‌المللی استفاده کنم؟',
      idnBody: 'بله. ورودی دامنه Unicode به IDNA ASCII A-label مانند xn--bcher-kva.example تبدیل می‌شود. از شکل A-label در رکوردهای DNS، فیلدهای کیف پول، فیلدهای رجیسترار و تنظیمات سرور استفاده کنید.',
      hostedSummary: 'ارائه‌دهنده DNS میزبانی‌شده باید چه چیزهایی پشتیبانی کند؟',
      hostedBody: 'ارائه‌دهنده باید میزبانی DNS مقتدر، امضای DNSSEC، خروجی DS یا DNSKEY، رکوردهای TLSA سفارشی، تازه‌سازی امضاها، رکوردهای denial احرازشده و ابزار کافی برای تایید اعتبارسنجی DNSSEC داشته باشد. اگر نتواند TLSA را در zone امضاشده منتشر کند، این مسیر DANE کامل نمی‌شود.'
    }
  },
  he: {
    languageName: 'עברית',
    languageLabel: 'שפה',
    hero: {
      eyebrow: 'צריכים רשומות HNS, רשם, DNSSEC ו-TLSA?',
      title: 'HNS DANE Bootstrap Generator',
      standard: 'אימות ישויות שמיות מבוסס DNS',
      steps: {
        enter: 'הזינו כאן את השם, ה-nameserver, כתובת ה-IP של השרת והתעודה.',
        send: 'שלחו את הרשומות שנוצרו לארנק HNS או לרשם.',
        zone: 'העתיקו את רשומות ה-zone לשרת ה-DNS הסמכותי.',
        done: 'פרסמו DNSSEC ו-TLSA כדי שאתר DANE יוכל לפעול.'
      }
    },
    sections: { domain: '1. דומיין', server: '2. שרת', dane: '3. DANE' },
    fields: {
      domainType: 'סוג דומיין',
      domainTypeHelp: 'HNS פירושו ארנק/משאב שם. ICANN פירושו לוח הרשם.',
      setupMode: 'מצב הגדרה',
      setupModeHelp: 'מצב delegated מצביע אל hostname של nameserver. SYNTH שומר כתובות IP של nameserver ב-HNS; שניהם עדיין צריכים DNS סמכותי חתום.',
      domain: 'דומיין',
      domainHelp: 'דוגמאות: dane/ או example.com',
      hnsDomainHelp: 'שמות HNS חייבים להסתיים ב-/ ולהשתמש בשם ASCII יחיד באותיות קטנות: a-z, 0-9, - או _, כאשר - ו-_ רק באמצע.',
      dnsServerPreset: 'preset של שרת DNS',
      dnsServerPresetHelp: 'זה משנה את דוגמת השרת להעתקה בלבד, לא את משמעות ה-DNS.',
      nameserverHost: 'hostname של nameserver',
      nameserverHostHelp: 'השתמשו ב-nameservers של הספק, או ב-ns1.yourname. עם glue אם ה-nameserver נמצא תחת אותו שם.',
      nameserverIpv4: 'IPv4 של nameserver',
      nameserverIpv4Help: 'נדרש עבור HNS SYNTH ועבור nameservers delegated בתוך ה-zone; אופציונלי עבור nameservers delegated חיצוניים.',
      nameserverIpv6: 'IPv6 של nameserver (אופציונלי)',
      nameserverIpv6Help: 'כתובת/glue אופציונלית ב-IPv6 עבור שרת ה-DNS.',
      websiteIpv4: 'IPv4 של האתר',
      websiteIpv4Help: 'כתובת IPv4 ציבורית של שרת ה-web ב-apex של הדומיין.',
      websiteIpv6: 'IPv6 של האתר (אופציונלי)',
      websiteIpv6Help: 'כתובת IPv6 ציבורית אופציונלית של שרת ה-web ב-apex של הדומיין.',
      port: 'פורט HTTPS',
      portHelp: '443 הוא ברירת המחדל עבור HTTPS רגיל. שנו רק אם שירות TLS משתמש בפורט אחר.',
      certificate: 'תעודת TLSA או PUBLIC KEY',
      certificateHelp: 'הדביקו כאן תעודת leaf או PEM PUBLIC KEY כדי ליצור רשומת TLSA. אין צורך במפתחות פרטיים.',
      dnskey: 'DNSKEY',
      dnskeyHelp: 'לאחר הפעלת חתימת DNSSEC בשרת ה-DNS, הדביקו כאן את DNSKEY של ה-zone כדי ליצור DS.'
    },
    options: {
      hns: 'Handshake / HNS',
      icann: 'ICANN / DNS',
      delegated: 'DNS סמכותי delegated',
      hnsInline: 'HNS SYNTH nameserver',
      genericZone: 'קובץ zone כללי',
      hostedDns: 'לוח ספק DNS מנוהל',
      powerdns: 'PowerDNS Authoritative',
      knot: 'Knot DNS',
      bind: 'BIND 9',
      windowsServer: 'Windows Server DNS',
      nsd: 'NSD'
    },
    howTo: {
      summary: 'איך להשיג את זה',
      certIntro: 'זהו שדה המקור של TLSA. הדביקו את התעודה הציבורית ששרת ה-TLS מגיש עבור ה-hostname, הפורט, הפרוטוקול ושם SNI המדויקים, או הדביקו PEM PUBLIC KEY תואם. אל תדביקו מפתח פרטי.',
      certFetch: 'אם יש לכם גישת shell, שלפו את תעודת ה-leaf מהשירות החי:',
      certFile: 'אם כבר יש לכם קובץ תעודה, הדביקו את הבלוק מ-BEGIN CERTIFICATE עד END CERTIFICATE. כדי להדביק רק את המפתח הציבורי מקובץ תעודה, הריצו:',
      dnskeyIntro: 'תחילה הפעילו חתימת DNSSEC על ה-child zone הסמכותי המדויק שמאצילים. שרת ה-DNS או ספק DNS מנוהל יוצר את ה-DNSKEY הציבורי; האפליקציה משתמשת ברשומה הציבורית הזו כדי ליצור רשומת DS בצד ה-parent.',
      dnskeyHosted: 'בלוח DNS מנוהל, חפשו הגדרות DNSSEC, DS או DNSKEY. אם הספק נותן רשומת DS ישירות, אפשר לפרסם אותה בארנק או אצל הרשם בלי להדביק כאן DNSKEY.',
      dnskeyQuery: 'אם השרת הסמכותי כבר עונה, שאילתו את ה-DNSKEY הציבורי והדביקו את שורת DNSKEY של ה-zone, בדרך כלל מפתח חתימה עם flags 257:'
    },
    copy: { nothing: '# עדיין לא נוצר דבר' },
    linode: {
      provisioningTag: 'הקמה בלחיצה אחת',
      cardLink: 'שרת HNS DANE ב-Linode/Akamai',
      cardBody: 'שרת HNS DANE בלחיצה אחת לשם Handshake יחיד. מתקין Knot DNS סמכותי עם DNSSEC, TLSA, לוח nginx, אימות מקומי ופקודות משאב HNS להעתקה והדבקה. לעולם לא מבקש seed של ארנק, אסימוני API של Linode, פרטי כניסה לרשם או נתוני תשלום.',
      deployTitle: 'פריסה ב-Linode/Akamai',
      beginHosting: 'התחלת אירוח',
      publishSetup: 'פרסום ההגדרה',
      guide: 'מדריך',
      stackScript: 'StackScript'
    },
    status: {
      title: 'מצב ההגדרה',
      ok: 'OK',
      warn: 'בדיקה',
      missing: 'נדרש',
      labels: { Domain: 'דומיין', 'Website IP': 'IP אתר', 'HNS inline': 'HNS SYNTH', DANE: 'DANE', Nameserver: 'Nameserver', Glue: 'Glue', DS: 'DS', TLSA: 'TLSA' }
    },
    notices: { title: 'דורש תשומת לב' },
    summary: {
      aria: 'סיכום ההגדרה הנוכחית',
      hnsInline: 'HNS SYNTH nameserver',
      delegated: 'DNSSEC + DANE delegated',
      glueRequired: 'נדרש glue',
      externalNameserver: 'Nameserver חיצוני',
      dsReady: 'DS מוכן',
      dsPlaceholder: 'DS חסר',
      tlsaReady: 'TLSA מוכן',
      tlsaPlaceholder: 'TLSA חסר'
    },
    output: {
      parentHns: 'שימו זאת בארנק HNS / משאב השם',
      parentIcann: 'שימו זאת אצל הרשם / zone האב',
      authoritative: 'שימו זאת על שרת ה-DNS הסמכותי',
      steps: 'בצעו את השלבים האלה',
      verify: 'אמתו עם הפקודות האלה',
      web: 'הערת שרת web',
      integrator: 'Integrator JSON',
      server: 'Preset שרת',
      audiences: { parent: 'parent', authoritative: 'סמכותי', server: 'שרת', web: 'web', verify: 'אימות', integrator: 'integrator' }
    },
    faq: {
      title: 'עזרה',
      setupModeSummary: 'באיזה מצב הגדרה להשתמש?',
      setupModeBody: 'השתמשו ב-DNS סמכותי delegated עבור מסלול הגדרת DANE: צרו או בחרו zone DNS סמכותי, שימו את hostname ה-nameserver שלו בארנק או אצל הרשם, הוסיפו glue אם ה-hostname נמצא תחת אותו שם, הפעילו DNSSEC, פרסמו DS בצד ה-parent, ופרסמו A/AAAA יחד עם TLSA ב-zone החתום. השתמשו ב-HNS SYNTH רק כאשר שם HNS צריך לשמור ישירות את IP ה-nameserver הסמכותי; SYNTH הוא עדיין רק הפניית nameserver של HNS לאותו zone DANE חתום.',
      domainSummary: 'איזה פורמט דומיין להזין?',
      domainBody: 'עבור HNS, הזינו שם יחיד באותיות קטנות שמסתיים ב-/, למשל dane/. אל תכללו labels ילדים כמו www.dane/ או / פנימי. עבור ICANN, הזינו שם דומיין DNS, למשל example.com.',
      presetSummary: 'איזה preset לבחור?',
      presetBody: 'השתמשו ב-Hosted DNS אם הספק תומך ב-DNSSEC וברשומות TLSA. השתמשו בקובץ zone כללי כאשר אתם מתאימים רשומות ל-BIND, Knot, NSD או שרת אחר. השתמשו ב-PowerDNS אם אתם רוצים DNS מבוסס מסד נתונים או API.',
      splitSummary: 'מה נכנס לארנק או לרשם לעומת שרת ה-DNS?',
      splitBody: 'הארנק או הרשם מקבלים NS/GLUE או SYNTH, וגם DS. שרת ה-DNS הסמכותי מקבל NS/A/AAAA/TLSA וחותם את ה-zone. TLSA היא רשומת שרת DNS, לא רשומת ארנק.',
      nameserverIpv4Summary: 'מתי צריך IP של nameserver?',
      nameserverIpv4Body: 'השתמשו בכתובת IPv4 הציבורית של שרת ה-DNS הסמכותי. במצב HNS SYNTH היא הופכת ל-SYNTH4. במצב delegated היא GLUE4 בצד ה-parent רק כאשר hostname ה-nameserver נמצא בתוך אותו שם או zone.',
      websiteIpv4Summary: 'האם זה IP של SYNTH או glue?',
      websiteIpv4Body: 'לא. IPv4 של האתר הופך לרשומת A של האתר על שרת ה-DNS הסמכותי. SYNTH ו-glue משמשים להגעה אל ה-nameserver; רשומות A/AAAA של האתר מכוונות דפדפנים אל שרת ה-web.',
      dnskeySummary: 'מתי להדביק DNSKEY?',
      dnskeyBody: 'הדביקו DNSKEY אחרי שה-child zone הסמכותי המדויק נחתם עם DNSSEC. הדף הזה משתמש בו כדי ליצור רשומת DS בצד ה-parent; הוא לא חותם את ה-zone ולא שומר מפתחות DNSSEC פרטיים. אמתו את השרשרת אחרי פרסום ה-DS.',
      idnSummary: 'אפשר להשתמש בשם דומיין בינלאומי?',
      idnBody: 'כן. קלט דומיין Unicode מומר ל-IDNA ASCII A-labels כגון xn--bcher-kva.example. השתמשו בצורת A-label ברשומות DNS, שדות ארנק, שדות רשם והגדרות שרת.',
      hostedSummary: 'מה ספק DNS מנוהל חייב לתמוך בו?',
      hostedBody: 'הספק צריך אירוח DNS סמכותי, חתימת DNSSEC, ייצוא DS או DNSKEY, רשומות TLSA מותאמות, רענון חתימות, רשומות denial מאומתות ומספיק אבחון כדי לאשר אימות DNSSEC. אם ספק לא יכול לפרסם TLSA ב-zone חתום, הוא לא יכול להשלים את מסלול DANE הזה.'
    }
  }
} as const;

export type LanguageCode = keyof typeof localeText;

type WidenStrings<T> = T extends string
  ? string
  : T extends object
    ? { readonly [K in keyof T]: WidenStrings<T[K]> }
    : T;

export type LocaleText = WidenStrings<typeof localeText.en>;

export const languageOptions = (Object.keys(localeText) as LanguageCode[]).map((code) => ({
  code,
  label: localeText[code].languageName
}));

export function isLanguageCode(value: unknown): value is LanguageCode {
  return typeof value === 'string' && value in localeText;
}

export function isRtlLanguage(value: LanguageCode): boolean {
  return value === 'ar' || value === 'fa' || value === 'he';
}
